import { useMemo } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isToday,
  isSameDay,
  format,
} from "date-fns";
import { cn } from "@/lib/utils";
import type { CalendarEvent } from "@/hooks/useCalendar";
import type { Item } from "@/lib/types";

const WEEKDAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

type Props = {
  currentMonth: Date;
  events: CalendarEvent[];
  items: Item[];
  selectedDate: Date | null;
  onSelectDate: (date: Date) => void;
};

export function MonthView({
  currentMonth,
  events,
  items,
  selectedDate,
  onSelectDate,
}: Props) {
  // Build grid of weeks
  const weeks = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const result: Date[][] = [];
    let day = gridStart;
    while (day <= gridEnd) {
      const week: Date[] = [];
      for (let i = 0; i < 7; i++) {
        week.push(day);
        day = addDays(day, 1);
      }
      result.push(week);
    }
    return result;
  }, [currentMonth]);

  // Index events by date string
  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const event of events) {
      const dateStr = event.start.slice(0, 10);
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(event);
    }
    return map;
  }, [events]);

  // Index items by date_target
  const itemsByDate = useMemo(() => {
    const map: Record<string, Item[]> = {};
    for (const item of items) {
      if (!item.date_target) continue;
      if (!map[item.date_target]) map[item.date_target] = [];
      map[item.date_target].push(item);
    }
    return map;
  }, [items]);

  return (
    <div>
      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAY_HEADERS.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-muted-foreground py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 border-t border-l border-border">
        {weeks.flat().map((day) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const inMonth = isSameMonth(day, currentMonth);
          const today = isToday(day);
          const selected = selectedDate ? isSameDay(day, selectedDate) : false;
          const dayEvents = eventsByDate[dateStr] || [];
          const dayItems = itemsByDate[dateStr] || [];
          const hasContent = dayEvents.length > 0 || dayItems.length > 0;

          return (
            <button
              key={dateStr}
              onClick={() => onSelectDate(day)}
              className={cn(
                "relative border-r border-b border-border p-1 min-h-[4.5rem] sm:min-h-[5.5rem] text-left transition-colors hover:bg-muted/50",
                !inMonth && "bg-muted/20",
                selected && "bg-primary/10 ring-1 ring-primary/40 ring-inset"
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "text-xs sm:text-sm leading-none inline-flex items-center justify-center",
                    today &&
                      "bg-primary text-primary-foreground rounded-full w-6 h-6 sm:w-7 sm:h-7 font-bold",
                    !today && !inMonth && "text-muted-foreground/40",
                    !today && inMonth && "text-foreground font-medium px-1"
                  )}
                >
                  {format(day, "d")}
                </span>
              </div>

              {/* Event & task indicators */}
              {hasContent && (
                <div className="mt-1 space-y-0.5">
                  {/* Show up to 2 event bars on desktop, dots on mobile */}
                  {dayEvents.slice(0, 2).map((evt) => (
                    <div
                      key={evt.id}
                      className="hidden sm:block text-[10px] leading-tight truncate rounded px-1 py-0.5 bg-blue-500/15 text-blue-600 dark:text-blue-400"
                    >
                      {evt.all_day ? "" : format(new Date(evt.start), "h:mma ")}{evt.title}
                    </div>
                  ))}
                  {dayEvents.length > 2 && (
                    <div className="hidden sm:block text-[10px] text-muted-foreground px-1">
                      +{dayEvents.length - 2} more
                    </div>
                  )}

                  {/* Mobile: colored dots */}
                  <div className="flex items-center gap-0.5 sm:hidden">
                    {dayEvents.length > 0 && (
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    )}
                    {dayItems.length > 0 && (
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    )}
                  </div>

                  {/* Task count badge (desktop) */}
                  {dayItems.length > 0 && (
                    <div className="hidden sm:block text-[10px] leading-tight truncate rounded px-1 py-0.5 bg-primary/10 text-primary">
                      {dayItems.length} task{dayItems.length !== 1 ? "s" : ""}
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
